// Helper functions to manage per-tab filter state in sessionStorage
// Make these globally available so facets.js can access them
window.getTabFilterState = function(tabIndex, collectionHandle) {
    try {
        const key = `collectionTab_${collectionHandle}_${tabIndex}`;
        const saved = sessionStorage.getItem(key);
        return saved ? JSON.parse(saved) : null;
    } catch (e) {
        return null;
    }
};

window.saveTabFilterState = function(tabIndex, collectionHandle, activePill, filterCheckboxes = null, sortBy = null) {
    try {
        const key = `collectionTab_${collectionHandle}_${tabIndex}`;
        const state = { activePill };
        
        // If filterCheckboxes provided, save them; otherwise keep existing
        if (filterCheckboxes !== null) {
            state.filterCheckboxes = filterCheckboxes;
        } else {
            // Preserve existing filter checkbox states
            const existing = window.getTabFilterState(tabIndex, collectionHandle);
            if (existing && existing.filterCheckboxes) {
                state.filterCheckboxes = existing.filterCheckboxes;
            }
        }
        
        // If sortBy provided, save it; otherwise try to get current value
        if (sortBy !== null) {
            state.sortBy = sortBy;
        } else {
            // Try to get the current sort value from DOM
            const collectionTabs = document.querySelector('.collection-tabs');
            if (collectionTabs) {
                const tabContent = collectionTabs.querySelector(`.tab-content[data-tab="${tabIndex}"]`);
                if (tabContent) {
                    const sortSelect = tabContent.querySelector('select[name="sort_by"], select[id^="CollectionSortBy"]');
                    if (sortSelect && sortSelect.value) {
                        state.sortBy = sortSelect.value;
                    }
                }
            }
            
            // If still no sortBy, preserve existing
            if (!state.sortBy) {
                const existing = window.getTabFilterState(tabIndex, collectionHandle);
                if (existing && existing.sortBy) {
                    state.sortBy = existing.sortBy;
                }
            }
        }
        
        sessionStorage.setItem(key, JSON.stringify(state));
    } catch (e) {
        // Silent failure
    }
};

window.clearTabFilterState = function(tabIndex, collectionHandle) {
    try {
        const key = `collectionTab_${collectionHandle}_${tabIndex}`;
        sessionStorage.removeItem(key);
    } catch (e) {
        // Silent failure
    }
};

// Keep local references for backward compatibility
const getTabFilterState = window.getTabFilterState;
const saveTabFilterState = window.saveTabFilterState;
const clearTabFilterState = window.clearTabFilterState;

// Get current filter checkbox states from the visible tab
function getCurrentFilterStates(tabContent) {
    const filterStates = {};
    const checkboxes = tabContent.querySelectorAll('input[type="checkbox"][name^="filter."]');
    
    checkboxes.forEach(checkbox => {
        const key = `${checkbox.name}::${checkbox.value}`;
        filterStates[key] = checkbox.checked;
    });
    
    return filterStates;
}

// Apply saved filter checkbox states to the visible tab
function restoreFilterStates(tabContent, filterStates) {
    if (!filterStates) {
        return;
    }
    
    // Set a flag to indicate we're restoring state (to prevent interference from other handlers)
    window.isRestoringCollectionTabFilters = true;
    
    const checkboxes = tabContent.querySelectorAll('input[type="checkbox"][name^="filter."]');
    
    checkboxes.forEach(checkbox => {
        const key = `${checkbox.name}::${checkbox.value}`;
        if (filterStates.hasOwnProperty(key)) {
            const shouldBeChecked = filterStates[key];
            if (checkbox.checked !== shouldBeChecked) {
                checkbox.checked = shouldBeChecked;
            }
        }
    });
    
    // Clear the flag after a short delay
    setTimeout(() => {
        window.isRestoringCollectionTabFilters = false;
    }, 100);
}

/* A tab opens with 48 cards: its first 16 come with the page (or the tab or facets
   render), pages 2 and 3 load in the background, then Load More adds 16 a tap, so
   the 4-across desktop grid always ends on a full row. Each page is its own request
   because Shopify resolves only 20 product groupings per render, so in one bigger
   render every style past the 20th loses its swatches. */
const TAB_OPEN_PAGES = 3;

/* Schedule a debounced background load of a tab's pages 2 to TAB_OPEN_PAGES */
function scheduleAutoLoadOpenPages() {
    if (window._autoLoadPage2Timeout) {
        clearTimeout(window._autoLoadPage2Timeout);
    }
    window._autoLoadPage2Timeout = setTimeout(async () => {
        const instance = window.infiniteScrollInstance;
        if (!instance || !instance.autoLoadNextPage) return;
        for (let page = 2; page <= TAB_OPEN_PAGES; page++) {
            // facets.js is about to replace this grid (the sort reset on a first visit,
            // a pill). Its productGridRendered tops up the new grid; loading now would
            // append to the old one and repeat cards.
            if (instance.getScope().dataset.facetsPending !== undefined) return;
            // Another tab took over infinite scroll while the last page loaded.
            if (window.infiniteScrollInstance !== instance) return;
            // Only load the page that comes next. A tab the shopper comes back to
            // already has its pages, so reopening it loads nothing, and a Load More
            // tap in between ends the top-up.
            const loadMoreBtn = instance.getScope().querySelector('[data-load-more]');
            const nextPageUrl = loadMoreBtn && loadMoreBtn.dataset.nextPageUrl;
            if (!nextPageUrl || new URL(nextPageUrl, window.location.origin).searchParams.get('page') !== String(page)) return;
            await instance.autoLoadNextPage();
        }
    }, 500);
}

/* Initialize infinite scroll for a specific tab */
function initializeInfiniteScrollForTab(tabContent) {
    // Find the load more button within this specific tab
    const loadMoreBtn = tabContent.querySelector('[data-load-more]');
    
    if (!loadMoreBtn) {
        return; // No load more button in this tab
    }
    
    // Reset all load more buttons' initialization flags
    document.querySelectorAll('[data-load-more]').forEach(btn => {
        delete btn.dataset.infiniteScrollInit;
    });
    
    // Check if InfiniteScroll class exists
    if (typeof InfiniteScroll === 'undefined') {
        console.warn('InfiniteScroll class not found');
        return;
    }
    
    // Reset the singleton instance by setting it to null
    // This allows us to create a new instance for the current tab
    if (window.infiniteScrollInstance) {
        // Remove old event listener if it exists
        if (window.infiniteScrollInstance.documentClickHandler) {
            document.removeEventListener('click', window.infiniteScrollInstance.documentClickHandler, true);
        }
        window.infiniteScrollInstance = null;
    }
    
    try {
        // Create new instance with the tab content as the scope element
        // This ensures this instance always operates on this specific tab
        const instance = new InfiniteScroll(tabContent);
        // Store it globally so we can reset it later
        window.infiniteScrollInstance = instance;

        // Load pages 2 and 3 in the background so the tab opens with 48 cards
        scheduleAutoLoadOpenPages();
    } catch (error) {
        console.error('Error initializing infinite scroll:', error);
    }
}

/* The tab's filter form's sort select, which shows the sort the grid was rendered
   with. The pill row's hidden select (CollectionSortBy) always starts on Featured. */
function getTabSortSelect(tabContent) {
    return tabContent.querySelector('.facets-unified select[name="sort_by"]')
        || tabContent.querySelector('select[name="sort_by"], select[id^="CollectionSortBy"]');
}

/* Initialize facets for a tab */
function initializeFacetsForTab(tabContent) {
    if (typeof FacetFiltersForm === 'undefined') {
        return;
    }
    
    const tabIndex = tabContent.dataset.tab;
    const collectionHandle = tabContent.dataset.collectionHandle;
    // Read before the sort reset below can start a new request for this tab
    const pendingParams = tabContent.dataset.facetsPending;

    // Wire up refinement menus for the tab
    if (FacetFiltersForm.wireRefinementMenus) {
        FacetFiltersForm.wireRefinementMenus();
    }
    
    // Run cleanup functions now that this tab is visible
    if (FacetFiltersForm.removeBooleanOptions) {
        FacetFiltersForm.removeBooleanOptions();
    }
    
    if (FacetFiltersForm.dedupeFilterGroups) {
        FacetFiltersForm.dedupeFilterGroups();
    }
    
    // Re-initialize category pills for the newly visible tab
    const facetForm = tabContent.querySelector('facet-filters-form');
    if (facetForm && facetForm.initCategoryPills) {
        facetForm.initCategoryPills();
    }
    
    // Restore saved filter state for this tab
    const savedState = getTabFilterState(tabIndex, collectionHandle);
    if (savedState) {
        // Restore sort dropdown value
        if (savedState.sortBy) {
            const sortSelect = getTabSortSelect(tabContent);
            if (sortSelect && sortSelect.value !== savedState.sortBy) {
                sortSelect.value = savedState.sortBy;
                // Adjust width if the function exists
                if (FacetFiltersForm && FacetFiltersForm.adjustSelectWidth) {
                    FacetFiltersForm.adjustSelectWidth(sortSelect);
                }
            }
        }
        
        // Restore filter checkbox states
        if (savedState.filterCheckboxes) {
            restoreFilterStates(tabContent, savedState.filterCheckboxes);
        }
        
        // Restore category pill state
        if (savedState.activePill) {
            // Find and click the saved pill to restore the filter
            const savedPill = tabContent.querySelector(`.category-pill[data-category-pill="${savedState.activePill}"]`);
            if (savedPill && !savedPill.classList.contains('active')) {
                // Simulate click to restore the filter
                setTimeout(() => {
                    savedPill.click();
                }, 100);
            }
        }
    } else {
        // No saved state - ensure "All" is selected
        // Update category pills active state for the newly visible tab
        if (FacetFiltersForm.updateCategoryPillsActiveState) {
            FacetFiltersForm.updateCategoryPillsActiveState();
        }

        // Force sort to "manual" (Featured) if the collection's default sort differs
        const sortSelect = getTabSortSelect(tabContent);
        if (sortSelect && sortSelect.value !== 'manual') {
            sortSelect.value = 'manual';
            if (FacetFiltersForm.adjustSelectWidth) {
                FacetFiltersForm.adjustSelectWidth(sortSelect);
            }
            // Trigger fetch with manual sort
            sortSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }
    
    // Open filter sections that have active values
    if (FacetFiltersForm.openActiveFilters) {
        FacetFiltersForm.openActiveFilters();
    }
    
    // Reinitialize select widths now that the tab is visible
    // This fixes the issue where select dropdowns were too narrow in hidden tabs
    if (FacetFiltersForm.reinitializeSelectWidths) {
        FacetFiltersForm.reinitializeSelectWidths();
    }

    // A grid this tab asked for came back (or was cancelled) after the shopper
    // switched away, so facets.js never painted it. Paint it now, from cache if it
    // came back; productGridRendered then sets up infinite scroll for this tab.
    if (pendingParams !== undefined) {
        FacetFiltersForm.renderPage(pendingParams, null, false);
        return;
    }

    // Initialize infinite scroll for this tab
    if (typeof InfiniteScroll !== 'undefined') {
        initializeInfiniteScrollForTab(tabContent);
    }
}

/* Update Collection Tab in view based on dropdown selection. */
function update_collection_tab(item){
    const collectionTabs = item.closest('.collection-tabs');
    
    // Save current tab's active category pill, filter states, and sort value before switching
    const currentTabContent = collectionTabs.querySelector('.tab-content.visible');
    if (currentTabContent) {
        const currentTabIndex = currentTabContent.dataset.tab;
        const currentCollectionHandle = currentTabContent.dataset.collectionHandle;
        const activePill = currentTabContent.querySelector('.category-pill.active');
        const filterStates = getCurrentFilterStates(currentTabContent);
        
        // Get current sort value
        const sortSelect = currentTabContent.querySelector('select[name="sort_by"], select[id^="CollectionSortBy"]');
        const sortBy = sortSelect ? sortSelect.value : null;
        
        if (activePill) {
            const activePillType = activePill.getAttribute('data-category-pill');
            saveTabFilterState(currentTabIndex, currentCollectionHandle, activePillType, filterStates, sortBy);
        }
    }
    
    // Remove active/visible from all tabs first
    const allTabButtons = collectionTabs.querySelectorAll('.tab');
    const allTabContents = collectionTabs.querySelectorAll('.tab-content');
    
    allTabButtons.forEach(btn => btn.classList.remove('active'));
    allTabContents.forEach(content => content.classList.remove('visible'));
    
    // Add active/visible to the selected tab
    const targetButton = collectionTabs.querySelector('.tab[data-tab="'+ item.dataset.tab +'"]');
    const targetContent = collectionTabs.querySelector('.tab-content[data-tab="'+ item.dataset.tab +'"]');
    
    if (targetButton) {
        targetButton.classList.add('active');
    }
    
    if (targetContent) {
        targetContent.classList.add('visible');

        // A tab that didn't render with the page shows its placeholder until it loads
        if (targetContent.dataset.tabSrc) {
            showTabWhenLoaded(item, targetContent);
            return;
        }

        // IMPORTANT: Use requestAnimationFrame to ensure the visible class is applied to DOM
        // before facets try to query for the visible tab
        requestAnimationFrame(() => {
            openTabContent(item, targetContent);
        });
    }

    // Note: We removed the filter clearing logic here because it was interfering
    // with the filter application system. The filtering system now properly
    // handles collection context switching through the facets.js AJAX system.
}

/* Set up a tab that is now on screen with its content in place */
function openTabContent(item, tabContent) {
    // Dispatch custom event
    const event = new CustomEvent('collectionTabChanged', {
        detail: {
            tabIndex: item.dataset.tab,
            collectionHandle: tabContent.dataset.collectionHandle,
            collectionUrl: tabContent.dataset.collectionUrl
        }
    });
    document.dispatchEvent(event);

    // Initialize facets (DOM and visible class are now ready)
    initializeFacetsForTab(tabContent);
}

/*
 * Tabs after the first don't render with the page: sections/collection-tabs.liquid
 * gives each a placeholder and data-tab-src, its own render from
 * sections/collection-tabs-tab.liquid. Requests are shared by src in
 * window.collectionTabLoads, which also holds one the page started for a ?tab= link.
 */
function loadTabContent(tabContent) {
    const loads = window.collectionTabLoads = window.collectionTabLoads || {};
    const src = tabContent.dataset.tabSrc;
    if (!loads[src]) {
        loads[src] = fetch(src).then((response) => {
            if (!response.ok) throw new Error(`Tab ${response.status}`);
            return response.text();
        });
    }
    return loads[src].catch((error) => {
        delete loads[src];
        throw error;
    });
}

/* Put a tab's fetched content in place of its placeholder. Only while it's on screen,
   so facets.js sets the new forms up against this tab (its scope is the visible tab). */
function fillTabContent(tabContent, html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const body = parsed.querySelector('.shopify-section') || parsed.body;
    if (!body.querySelector('#product-grid')) throw new Error('Tab has no grid');

    // facets.js gives each .facets-container a backdrop only on its first run, and a
    // filter form's drawer uses the backdrops on the page as it goes in, so add
    // this tab's before it does
    const backdrop = document.querySelector('.facets-backdrop');
    if (backdrop) {
        body.querySelectorAll('.facets-container').forEach((container) => {
            const copy = backdrop.cloneNode(false);
            copy.classList.remove('active');
            container.appendChild(copy);
        });
    }

    const loading = tabContent.querySelector('[data-tab-loading]');
    if (loading) loading.remove();
    tabContent.append(...body.childNodes);
    delete tabContent.dataset.tabSrc;

    // As facets.js does for a grid it swaps in
    tabContent.querySelectorAll('.scroll-trigger').forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
    });
    // Lazy cards hold their real sizes in data-srcset and a 320px src. lazysizes only
    // loads where native lazy loading is missing, so the polyfill is what swaps them in
    // (it falls back to lazysizes itself)
    if (window.lazyloadPolyfill) {
        window.lazyloadPolyfill(tabContent);
    }
    if (window.convertCurrency) {
        window.convertCurrency(tabContent);
    }
    if (typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.addMobileFacetsCloseHandler) {
        FacetFiltersForm.addMobileFacetsCloseHandler();
    }
}

function showTabWhenLoaded(item, tabContent) {
    const error = tabContent.querySelector('[data-tab-error]');
    if (error) error.hidden = true;

    loadTabContent(tabContent)
        .then((html) => {
            // Shown again later (that visit fills it), or already filled by an earlier visit
            if (!tabContent.dataset.tabSrc || !tabContent.classList.contains('visible')) return;
            fillTabContent(tabContent, html);
            requestAnimationFrame(() => openTabContent(item, tabContent));
        })
        .catch((e) => {
            console.error('Collection tab failed to load:', e);
            if (!tabContent.dataset.tabSrc) return;
            // Try again fetches afresh, also when the response came back without a grid
            if (window.collectionTabLoads) delete window.collectionTabLoads[tabContent.dataset.tabSrc];
            if (error) error.hidden = false;
        });
}

/* A phone opens the tab's filter drawer with Sort By open, as the Athos grid does.
   The drawer is the desktop sidebar's form, where Sort starts closed, so open it on
   the drawer's first opening only (closing it after that sticks). */
document.addEventListener('click', (e) => {
    const trigger = e.target.closest('.collection-tabs .tab-content .mobile-facets__trigger');
    if (!trigger) return;
    const tabContent = trigger.closest('.tab-content');
    if (tabContent.dataset.drawerOpened !== undefined) return;
    tabContent.dataset.drawerOpened = '';
    const sort = tabContent.querySelector('.facets-unified .refinementButton-sort[data-filter="Sort"] details');
    if (sort && !sort.open) sort.open = true;
}, true);

document.addEventListener('click', (e) => {
    const retry = e.target.closest('.collection-tabs [data-tab-retry]');
    if (!retry) return;
    const tabContent = retry.closest('.tab-content');
    const item = tabContent && tabContent.closest('.collection-tabs').querySelector(`.tab[data-tab="${tabContent.dataset.tab}"]`);
    if (item && tabContent.classList.contains('visible')) showTabWhenLoaded(item, tabContent);
});

/* Fetch the unloaded tabs once the page has settled, one at a time, so opening one
   is instant. Only the text is kept: a tab goes into the page when it's opened. */
function warmCollectionTabs() {
    if (warmCollectionTabs.started) return;
    warmCollectionTabs.started = true;
    if (navigator.connection && navigator.connection.saveData) return;
    const pending = [...document.querySelectorAll('.collection-tabs .tab-content[data-tab-src]')];
    pending.reduce((chain, tabContent) => chain.then(() => {
        if (!tabContent.dataset.tabSrc) return null;
        return loadTabContent(tabContent).catch(() => {});
    }), Promise.resolve());
}

// On idle after load, or 2s after the DOM is ready if apps hold load back longer
if (document.querySelector('.collection-tabs .tab-content[data-tab-src]')) {
    const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 1000));
    const warmSoon = () => idle(warmCollectionTabs, { timeout: 3000 });
    window.addEventListener('load', warmSoon);
    setTimeout(warmSoon, 2000);
}

// Initialize tabs on page load using event-driven approach
function initializeCollectionTabs() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const tab = urlParams.get('tab');
    const requestedTab = document.querySelector('.collection-tabs .tab[data-variable="'+tab+'"');
    const firstTab = document.querySelector('.collection-tabs button.tab:first-child');

    if( tab && requestedTab ){
        requestedTab.click();
    } else if( firstTab ){
        firstTab.click();
    }
}

// Listen for category pill clicks and save the active state
document.addEventListener('click', function(e) {
    const categoryPill = e.target.closest('.category-pill');
    if (!categoryPill) return;
    
    // Check if we're in collection tabs
    const collectionTabs = categoryPill.closest('.collection-tabs');
    if (!collectionTabs) return;
    
    // Get the current tab info
    const tabContent = categoryPill.closest('.tab-content');
    if (!tabContent) return;
    
    const tabIndex = tabContent.dataset.tab;
    const collectionHandle = tabContent.dataset.collectionHandle;
    const pillType = categoryPill.getAttribute('data-category-pill');
    
    // Save the clicked pill state with current sort value
    setTimeout(() => {
        const filterStates = getCurrentFilterStates(tabContent);
        const sortSelect = tabContent.querySelector('select[name="sort_by"], select[id^="CollectionSortBy"]');
        const sortBy = sortSelect ? sortSelect.value : null;
        
        saveTabFilterState(tabIndex, collectionHandle, pillType, filterStates, sortBy);
    }, 100);
});

// Listen for filter checkbox changes and sort dropdown changes in collection tabs and save state
document.addEventListener('change', function(e) {
    // Check if we're in collection tabs first
    const collectionTabs = e.target.closest('.collection-tabs');
    if (!collectionTabs) {
        return;
    }
    
    // Get the current tab info
    const tabContent = e.target.closest('.tab-content.visible');
    if (!tabContent) {
        return;
    }
    
    const tabIndex = tabContent.dataset.tab;
    const collectionHandle = tabContent.dataset.collectionHandle;
    
    // Handle sort dropdown changes
    if (e.target.tagName === 'SELECT' && (e.target.name === 'sort_by' || e.target.id && e.target.id.startsWith('CollectionSortBy'))) {
        const sortBy = e.target.value;
        
        // Get current states
        const activePill = tabContent.querySelector('.category-pill.active');
        const activePillType = activePill ? activePill.getAttribute('data-category-pill') : 'all';
        const filterStates = getCurrentFilterStates(tabContent);
        
        // Save with the new sort value
        saveTabFilterState(tabIndex, collectionHandle, activePillType, filterStates, sortBy);
        return;
    }
    
    // Handle filter checkboxes
    if (e.target.type === 'checkbox' && e.target.name && e.target.name.startsWith('filter.')) {
        // Skip if we're currently restoring state
        if (window.isRestoringCollectionTabFilters) {
            return;
        }
        
        // Skip if we're clearing all filters (to prevent saving the cleared state)
        if (window.skipCollectionTabRestoration) {
            return;
        }
        
        // Capture the clicked checkbox details NOW (before any async operations)
        const clickedCheckbox = {
            name: e.target.name,
            value: e.target.value,
            checked: e.target.checked
        };
        
        // Use setTimeout to ensure state is captured after checkbox updates
        setTimeout(() => {
            const filterStates = getCurrentFilterStates(tabContent);
            
            // Ensure the clicked checkbox state is correctly reflected
            const key = `${clickedCheckbox.name}::${clickedCheckbox.value}`;
            filterStates[key] = clickedCheckbox.checked;
            
            const activePill = tabContent.querySelector('.category-pill.active');
            const activePillType = activePill ? activePill.getAttribute('data-category-pill') : 'all';
            
            // Save state - sortBy will be auto-captured from DOM in saveTabFilterState
            saveTabFilterState(tabIndex, collectionHandle, activePillType, filterStates);
        }, 10); // Slightly longer delay to ensure DOM updates
    }
}, true); // Use capture phase to ensure we save before other handlers

// Re-initialize infinite scroll and load pages 2 and 3 after facets.js re-renders the grid
// (e.g. after sort change, filter change, or category pill click)
document.addEventListener('productGridRendered', function() {
    const collectionTabs = document.querySelector('.collection-tabs');
    if (!collectionTabs) return;

    const activeTab = collectionTabs.querySelector('.tab-content.visible');
    if (!activeTab) return;

    initializeInfiniteScrollForTab(activeTab);
});

// Wait for both DOM and facets.js to be ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCollectionTabs);
} else {
    initializeCollectionTabs();
}

// Clear all collection tab filter states when navigating away from the page
window.addEventListener('beforeunload', function() {
    // Only clear if we're on a collection tabs page
    const collectionTabs = document.querySelector('.collection-tabs');
    if (collectionTabs) {
        try {
            // Get all tab contents to find their collection handles
            const allTabContents = collectionTabs.querySelectorAll('.tab-content');
            allTabContents.forEach(tabContent => {
                const tabIndex = tabContent.dataset.tab;
                const collectionHandle = tabContent.dataset.collectionHandle;
                if (tabIndex && collectionHandle) {
                    window.clearTabFilterState(tabIndex, collectionHandle);
                }
            });
        } catch (e) {
            // Silent failure
        }
    }
});

// Also clear session storage when clicking on navigation links that leave the collection tabs page
document.addEventListener('click', function(e) {
    const link = e.target.closest('a[href]');
    if (!link) return;
    
    // Check if we're on a collection tabs page
    const collectionTabs = document.querySelector('.collection-tabs');
    if (!collectionTabs) return;
    
    const href = link.getAttribute('href');
    
    // Skip if it's just a hash link or empty
    if (!href || href.startsWith('#') || href === '') return;
    
    // Skip if it's the current collection tabs page (tab switching)
    if (link.closest('.collection-tabs')) {
        // Allow tab switching within the same collection tabs component
        if (link.classList.contains('tab')) return;
    }
    
    // Clear all tab filter states when navigating to a different page
    try {
        const allTabContents = collectionTabs.querySelectorAll('.tab-content');
        allTabContents.forEach(tabContent => {
            const tabIndex = tabContent.dataset.tab;
            const collectionHandle = tabContent.dataset.collectionHandle;
            if (tabIndex && collectionHandle) {
                window.clearTabFilterState(tabIndex, collectionHandle);
            }
        });
    } catch (e) {
        // Silent failure
    }
});

/* A tab opened from down the grid starts at its first row, with the tab bar still
   stuck at the top, instead of at the depth the last tab was scrolled to. Only for
   a shopper's tap: the page clicks the first tab itself on load. */
document.addEventListener('click', (e) => {
    if (!e.isTrusted) return;
    const tab = e.target.closest('.collection-tabs .tabs .tab');
    if (!tab) return;
    const collectionTabs = tab.closest('.collection-tabs');
    const bar = collectionTabs.querySelector('.tabs-header');
    const content = collectionTabs.querySelector('.tabs-content');
    if (!bar || !content) return;
    const contentTop = content.getBoundingClientRect().top;
    const barBottom = bar.getBoundingClientRect().bottom;
    // The grid's first row is already in view
    if (contentTop >= barBottom - 1) return;
    // Scrolling up would otherwise bring the site header back down and move the tab bar
    const stickyHeader = document.querySelector('sticky-header');
    if (stickyHeader) stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    window.scrollTo({ top: window.scrollY + contentTop - barBottom, behavior: 'instant' });
});